import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { Badge, type ColorlessIconProps, IconDocNewspaper, LineBreak, UserBarItem } from '@direct-frontend/components';
import { matchPath, useLocation } from '@direct-frontend/modules-router';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { useDashboardMessagesUnreadCountLazy } from '#entities/dashboardMessage';
import { useDeferred } from '#shared/lib/hooks';
import { useNavigateWithSearchParams } from '#shared/lib/router';
import { notificationsRoute } from '#shared/lib/routes';
import { Tooltip } from '#shared/ui/Tooltip';
import { useDashboardNewsPresentation } from './useDashboardNewsPresentation';
import styles from './DashboardNewsLink.module.css';

export const DashboardNewsLink = ({ className }: { className?: string }) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const deferred = useDeferred();
    const navigate = useNavigateWithSearchParams();
    const location = useLocation();
    const isActive = matchPath(notificationsRoute.route, location.pathname) !== null;

    /* Запрос уходит только после первого idle — счётчик не нужен при первом рендере */
    const [fetchUnreadCount, { data: unreadData }] = useDashboardMessagesUnreadCountLazy();

    useEffect(() => {
        if (deferred) {
            fetchUnreadCount();
        }
    }, [deferred, fetchUnreadCount]);

    const { isTooltipVisible, hasUnreadMessages, dismissTooltip, markCurrentNewsAsSeen } = useDashboardNewsPresentation(
        unreadData?.unread_count,
        !isActive
    );

    const NewsIcon = useMemo(
        () =>
            forwardRef<SVGSVGElement, ColorlessIconProps>(function NewsIcon(props, ref) {
                return (
                    <IconDocNewspaper
                        {...props}
                        ref={ref}
                        color="gray"
                        className={styles.icon}
                        role={isActive || hasUnreadMessages ? 'main' : 'supplementary'}
                    />
                );
            }),
        [isActive, hasUnreadMessages]
    );

    const handleButtonClick = useCallback(() => {
        logEventString('RightSidePanel.NewsLinkClick', String(isActive));
        markCurrentNewsAsSeen();
        navigate(notificationsRoute.getUrl());
    }, [isActive, markCurrentNewsAsSeen, navigate]);

    return (
        <>
            <Tooltip
                title={
                    <>
                        Загляните, есть
                        <LineBreak mode="compact" />
                        новости для вас
                    </>
                }
                strategy="fixed"
                hasClose
                onClose={dismissTooltip}
                data-testid="DashboardNews"
                color="info"
                maxWidth="240"
                placement="left"
                isOpen={isTooltipVisible}
                triggerRef={triggerRef}
                isDismissable={false}
                className={styles.tooltip}
            />
            <div title="Открыть панель новостей" className={cn(styles.buttonContainer, className)}>
                <UserBarItem
                    className={styles.button}
                    ref={triggerRef}
                    icon={NewsIcon}
                    onClick={handleButtonClick}
                    isActive={isActive}
                />
                {hasUnreadMessages ? <Badge className={styles.badge} color="info" size="12" /> : null}
            </div>
        </>
    );
};
